/**
 * Arcanist vanilla text splitting — SplitText substitute (no GSAP Club license).
 *
 * splitLines always measures rendered wraps via offsetTop after inserting
 * inline-block word spans (<br> tags force segment breaks before measurement).
 *
 * SWAP POINT: If a GSAP Club license is acquired, replace calls to
 * ArcanistTextSplit.splitWords / splitLines with GSAP SplitText in
 * arcanist-reveal.js (and any section scripts that split text).
 */
(function (global) {
  "use strict";

  var WORD_CLASS = "arcanist-split-word";
  var LINE_CLASS = "arcanist-split-line";
  var BR_CLASS = "arcanist-split-br";

  /**
   * Wrap each word in an inline-block span. Returns the word span elements.
   * @param {HTMLElement} element
   * @returns {HTMLElement[]}
   */
  function splitWords(element) {
    if (element.dataset.arcanistSplitWords === "true") {
      return Array.prototype.slice.call(element.querySelectorAll("." + WORD_CLASS));
    }

    var text = element.textContent.trim();
    element.textContent = "";
    element.dataset.arcanistSplitWords = "true";

    if (!text) return [];

    var words = text.split(/\s+/);
    var spans = [];

    words.forEach(function (word, index) {
      var span = document.createElement("span");
      span.className = WORD_CLASS;
      span.style.display = "inline-block";
      span.textContent = word;
      element.appendChild(span);
      spans.push(span);
      if (index < words.length - 1) {
        element.appendChild(document.createTextNode(" "));
      }
    });

    return spans;
  }

  /**
   * Parse innerHTML into text segments separated by <br> (whitespace normalized).
   * @param {HTMLElement} element
   * @returns {string[]}
   */
  function parseLineSegments(element) {
    var html = element.innerHTML.trim();
    if (!html) return [];

    return html.split(/<br\s*\/?>/i).map(function (part) {
      var tmp = document.createElement("div");
      tmp.innerHTML = part;
      return tmp.textContent.replace(/\s+/g, " ").trim();
    });
  }

  /**
   * Insert inline-block word spans (and block break markers between <br> segments).
   * @param {HTMLElement} element
   * @param {string[]} segments
   * @returns {HTMLElement[]}
   */
  function buildWordSpansForLines(element, segments) {
    element.innerHTML = "";
    element.dataset.arcanistSplitWords = "true";

    var wordSpans = [];

    segments.forEach(function (segment, segIndex) {
      if (segIndex > 0) {
        var brMarker = document.createElement("span");
        brMarker.className = BR_CLASS;
        brMarker.setAttribute("aria-hidden", "true");
        brMarker.style.display = "block";
        brMarker.style.height = "0";
        brMarker.style.overflow = "hidden";
        element.appendChild(brMarker);
      }

      if (!segment) return;

      var words = segment.split(/\s+/).filter(Boolean);
      words.forEach(function (word, wordIndex) {
        var span = document.createElement("span");
        span.className = WORD_CLASS;
        span.style.display = "inline-block";
        span.textContent = word;
        element.appendChild(span);
        wordSpans.push(span);
        if (wordIndex < words.length - 1) {
          element.appendChild(document.createTextNode(" "));
        }
      });
    });

    return wordSpans;
  }

  /**
   * Group word spans into visual lines by offsetTop (wrap-aware).
   * @param {HTMLElement} element
   * @param {HTMLElement[]} wordSpans
   * @returns {HTMLElement[]}
   */
  function groupWordsIntoLines(element, wordSpans) {
    if (!wordSpans.length) return [];

    var groups = [];
    var currentGroup = [];
    var currentTop = null;

    wordSpans.forEach(function (span) {
      var top = span.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) <= 1) {
        currentGroup.push(span);
        if (currentTop === null) currentTop = top;
      } else {
        groups.push(currentGroup);
        currentGroup = [span];
        currentTop = top;
      }
    });
    if (currentGroup.length) groups.push(currentGroup);

    element.innerHTML = "";
    element.dataset.arcanistSplitLines = "true";

    var lineElements = [];
    groups.forEach(function (group) {
      var line = document.createElement("span");
      line.className = LINE_CLASS;
      line.style.display = "block";
      group.forEach(function (wordSpan, i) {
        line.appendChild(wordSpan);
        if (i < group.length - 1) {
          line.appendChild(document.createTextNode(" "));
        }
      });
      element.appendChild(line);
      lineElements.push(line);
    });

    return lineElements;
  }

  /**
   * Split element into line blocks for line-by-line reveal animation.
   * Always measures rendered wraps after layout (offsetTop grouping).
   * Explicit <br> tags force segment breaks before measurement.
   * @param {HTMLElement} element
   * @returns {HTMLElement[]}
   */
  function splitLines(element) {
    if (element.dataset.arcanistSplitLines === "true") {
      return Array.prototype.slice.call(element.querySelectorAll("." + LINE_CLASS));
    }

    var segments = parseLineSegments(element);
    if (!segments.length) return [];

    element.dataset.arcanistSplitOriginal = element.innerHTML;

    var wordSpans = buildWordSpansForLines(element, segments);
    if (!wordSpans.length) {
      delete element.dataset.arcanistSplitOriginal;
      delete element.dataset.arcanistSplitWords;
      element.innerHTML = "";
      return [];
    }

    return groupWordsIntoLines(element, wordSpans);
  }

  /**
   * Revert split markup to original HTML (theme editor section unload).
   * @param {HTMLElement} element
   */
  function revert(element) {
    if (!element || (!element.dataset.arcanistSplitWords && !element.dataset.arcanistSplitLines)) {
      return;
    }

    if (element.dataset.arcanistSplitOriginal != null) {
      element.innerHTML = element.dataset.arcanistSplitOriginal;
      delete element.dataset.arcanistSplitOriginal;
    } else {
      element.textContent = element.textContent;
    }

    delete element.dataset.arcanistSplitWords;
    delete element.dataset.arcanistSplitLines;
  }

  global.ArcanistTextSplit = {
    splitWords: splitWords,
    splitLines: splitLines,
    revert: revert,
  };
})(typeof window !== "undefined" ? window : this);
